/**
 * @file Drivers.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Monday, 30th March 2020 10:16:01 am
 * @copyright 2015 - 2020 SKAT LLC, Delive LLC
 * @flow
 */
import * as React from 'react'
import { CAST } from 'web-panel-essentials/misc'
import { FastLayer, Rect, Shape, Line, Text } from 'react-konva'

const Points = (props) => {
  return (
    <Shape
      sceneFunc={(context, shape) => {
        props.drivers.forEach(driver => {
          context.fillRect(driver.x - 2, driver.y - 2, 4, 4)
        })
        context.fillStrokeShape(shape)
      }}
      fill='black'
      strokeWidth={4}
    />)
}

const DriverMarker = (props: {x: number, y: number, code: string, zoom: number, status: number, count: number, ...}) => {
  const { x, y, code, zoom } = props
  if (!x) return null
  if (zoom < 7) return null

  const fontSize = zoom - 2
  const width = Math.max(Math.min(50, code.length * zoom), 2 * zoom)
  const offsetY = -(fontSize + 4) * 2
  return (
    <>
      <Rect transformsEnabled='position' perfectDrawEnabled={false} opacity={0.6} x={x} y={offsetY + y} fill='black' height={fontSize + 4} width={width} />
      <Text transformsEnabled='position' perfectDrawEnabled={false} x={x} y={offsetY + y + 2} fontStyle='bold' fill='white' ellipsis width={width} align='center' text={code} wrap='none' fontSize={fontSize} />
      <Line transformsEnabled='position' perfectDrawEnabled={false} points={[x, offsetY + y, x, offsetY + y + (fontSize + 4) * 2]} stroke='black' />
    </>
  )
}

export type Props = {
  drivers: Array<any>,
  bounds: any,
  latLngToPixel: Function,
  zoom: number,
  isAnimating: boolean
}

export type State = {
  visibleDrivers: Array<{code: string, status: number, x: number, y: number, count: number, latitude: number, longitude: number, ...}>
}

class Drivers extends React.Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = { visibleDrivers: [] }
  }

  UNSAFE_componentWillReceiveProps (nextProps: Props) { //eslint-disable-line
    let drivers: Array<{code: string, status: number, x: number, y: number, count: number, latitude: number, longitude: number, ...}>
    let minLat = nextProps.drivers[0] ? CAST.Number(nextProps.drivers[0].latitude) : 0
    let minLon = nextProps.drivers[0] ? CAST.Number(nextProps.drivers[0].longitude) : 0
    if (!nextProps.isAnimating) {
      drivers = nextProps.drivers.filter((driver) => {
        driver.latitude = CAST.Number(driver.latitude) // This is a nasty hack, but it ensures that driver has coordinates as numbers
        driver.longitude = CAST.Number(driver.longitude)
        if (driver.latitude < minLat) minLat = driver.latitude
        if (driver.longitude < minLon) minLon = driver.longitude

        return (
          driver.latitude >= nextProps.bounds.sw[0] &&
          driver.latitude <= nextProps.bounds.ne[0] &&
          driver.longitude >= nextProps.bounds.sw[1] &&
          driver.longitude <= nextProps.bounds.ne[1]
        )
      })

      if (drivers.length > 50 || nextProps.zoom < 11) {
        const points = {}

        let [minX, minY] = nextProps.latLngToPixel([minLat, minLon])

        minX = Math.round(minX)
        minY = Math.round(minY)

        drivers = drivers.reduce((list, driver) => {
          const [x, y] = nextProps.latLngToPixel([driver.latitude, driver.longitude])

          const cx = (Math.round(x) - minX) >> 3
          const cy = (Math.round(y) - minY) >> 3

          if (!points[cx]) points[cx] = {}
          if (points[cx][cy]) {
            points[cx][cy].count++
            points[cx][cy].x += x
            points[cx][cy].y += y
            points[cx][cy].latitude += driver.latitude
            points[cx][cy].longitude += driver.longitude
          } else {
            points[cx][cy] = {
              code: driver.code,
              status: driver.status,
              latitude: driver.latitude,
              longitude: driver.longitude,
              count: 1,
              x,
              y
            }
            list.push(points[cx][cy])
          }

          return list
        }, [])

        drivers.forEach((driver) => {
          driver.x = Math.round(driver.x / driver.count)
          driver.y = Math.round(driver.y / driver.count)
          driver.latitude /= driver.count
          driver.longitude /= driver.count
        })
      } else {
        drivers = drivers.map((driver) => {
          const [x, y] = nextProps.latLngToPixel([driver.latitude, driver.longitude])
          return { ...driver, x: Math.round(x), y: Math.round(y) }
        })
      }

      this.setState({
        visibleDrivers: drivers
      })
    } else {
      this.setState((state, props) => {
        return {
          visibleDrivers: state.visibleDrivers.map((driver) => {
            const [x, y] = props.latLngToPixel([driver.latitude, driver.longitude])
            return { ...driver, x: Math.round(x), y: Math.round(y) }
          })
        }
      })
    }
  }

  render (): React.Node {
    const { zoom } = this.props
    const { visibleDrivers } = this.state
    const heavyLoad = visibleDrivers.length > 50

    return (
      <FastLayer>
        {
          heavyLoad || zoom < 11
            ? <Points drivers={visibleDrivers} />
            : visibleDrivers.map((props, index) => <DriverMarker {...props} zoom={zoom} />)
        }
      </FastLayer>)
  }
}

export default Drivers
